<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        系統設定管理
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          新增設定
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="configList"
        :loading="loading"
        hide-default-footer
        :items-per-page="-1"
      >
        <template v-slot:item.level="{ item }">
          <v-chip :color="item.level === 2 ? 'success' : 'warning'" small>
            {{ levelText(item.level) }}
          </v-chip>
        </template>

        <template v-slot:item.updated_at="{ item }">
          {{ formatDate(item.updated_at) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click="openEditDialog(item)">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- 新增/編輯 Dialog -->
    <v-dialog v-model="dialog" max-width="800px" scrollable>
    <v-card>
        <v-card-title>
        <span class="text-h5">{{ isEdit ? '編輯設定' : '新增設定' }}</span>
        </v-card-title>

        <v-card-text style="max-height: 600px;" class="pa-4">
            <v-form ref="form" v-model="formValid">
                <v-text-field
                v-model="formData.id"
                label="ID"
                :disabled="isEdit"
                :rules="idRules"
                outlined
                dense
                ></v-text-field>

                <v-select
                v-model="formData.level"
                :items="levelOptions"
                label="權限等級"
                :rules="levelRules"
                outlined
                dense
                ></v-select>

                <v-card outlined class="mb-3">
                <v-card-subtitle class="d-flex align-center pb-0">
                    Setting (JSON 格式)
                </v-card-subtitle>
                <v-card-text class="pa-3">
                    <vue-json-editor
                    v-model="formData.setting"
                    :showBtns="false"
                    :mode="'code'"
                    @json-change="onJsonChange"
                    lang="zh"
                    :expandedOnStart="true"
                    />
                </v-card-text>
                <v-card-text v-if="jsonError" class="error--text py-2 pt-0">
                    <v-icon small color="error">mdi-alert-circle</v-icon>
                    {{ jsonError }}
                </v-card-text>
                </v-card>
            </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="closeDialog">取消</v-btn>
        <v-btn
            color="primary"
            @click="saveConfig"
            :loading="saving"
        >
            儲存
        </v-btn>
        </v-card-actions>
    </v-card>
    </v-dialog>

    <!-- 提示訊息 -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">關閉</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import vueJsonEditor from 'vue-json-editor'

import {
  apiGetSystemConfig,
  apiCreateSystemConfig,
} from '@/api/admin.js'

export default {
  name: 'SystemConfig',
  components: {
    vueJsonEditor
  },
  data() {
    return {
      loading: false,
      configList: [],
      dialog: false,
      isEdit: false,
      formValid: false,
      saving: false,
      formData: {
        id: '',
        level: null,
        setting: {}
      },
      jsonError: '',
      hasJsonError: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      
      headers: [
        { text: 'ID', value: 'id', sortable: false },
        { text: '權限等級', value: 'level', sortable: false },
        { text: '更新時間', value: 'updated_at', sortable: false },
        { text: '操作', value: 'actions', sortable: false, align: 'center' }
      ],
      
      levelOptions: [
        { text: '需驗證', value: 1 },
        { text: '公開', value: 2 }
      ],
      
      idRules: [
        v => !!v || 'ID 為必填',
      ],
      
      levelRules: [
        v => v !== null && v !== undefined || '請選擇權限等級',
      ]
    }
  },
  
  computed: {
    canSave() {
      return this.formValid && !this.hasJsonError
    }
  },
  
  mounted() {
    this.loadConfigs()
  },
  
  methods: {
    async loadConfigs() {
      this.loading = true
      try {
        const response = await apiGetSystemConfig()
        if (response.ok === 1) {
          this.configList = response.data
        }
      } catch (error) {
        this.showSnackbar('載入失敗：' + error.message, 'error')
      } finally {
        this.loading = false
      }
    },
    
    openCreateDialog() {
      this.isEdit = false
      this.formData = {
        id: '',
        level: null,
        setting: {}
      }
      this.jsonError = ''
      this.hasJsonError = false
      this.dialog = true
      
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    },
    
    openEditDialog(item) {
      this.isEdit = true
      
      // 深拷貝 setting
      let settingObj = {}
      if (typeof item.setting === 'string') {
        try {
          settingObj = JSON.parse(item.setting)
        } catch (e) {
          settingObj = {}
        }
      } else {
        settingObj = JSON.parse(JSON.stringify(item.setting))
      }
      
      this.formData = {
        id: item.id,
        level: item.level,
        setting: settingObj
      }
      
      this.jsonError = ''
      this.hasJsonError = false
      this.dialog = true
      
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.resetValidation()
        }
      })
    },
    
    closeDialog() {
      this.dialog = false
      this.formData = {
        id: '',
        level: null,
        setting: {}
      }
      this.jsonError = ''
      this.hasJsonError = false
    },
    
    onJsonChange(value) {
      // JSON 變更時的回調
      this.formData.setting = value
    },
    
    async saveConfig() {
      if (!this.$refs.form.validate()) {
        return
      }
      
      this.saving = true
      
      try {
        const params = {
          id: this.formData.id,
          level: this.formData.level,
          setting: this.formData.setting
        }
        await apiCreateSystemConfig(params, this.formData.id)
        if (this.isEdit) {
          this.showSnackbar('更新成功', 'success')
        } else {
          this.showSnackbar('新增成功', 'success')
        }
        
        this.closeDialog()
        this.loadConfigs()
      } catch (error) {
        this.showSnackbar('儲存失敗：' + error.message, 'error')
      } finally {
        this.saving = false
      }
    },
    
    levelText(level) {
      const option = this.levelOptions.find(opt => opt.value === level)
      return option ? option.text : level
    },
    
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    
    showSnackbar(text, color) {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>

<style>
/* vue-json-editor 的樣式 */
.jsoneditor-vue {
  height: 400px;
}

.jsoneditor {
  border: 1px solid #d9d9d9;
}

.jsoneditor-menu {
  background-color: #2c3e50;
  border-bottom: 1px solid #2c3e50;
}

.jsoneditor-mode-code .ace-jsoneditor {
  background-color: #272822;
  color: #f8f8f2;
}

.jsoneditor-mode-code .ace-jsoneditor .ace_gutter {
  background-color: #272822;
  color: #999;
}
</style>